"use client";

import { CheckCircleIcon } from "@phosphor-icons/react";
import { CheckCircle2, CircleAlert, CircleDashed, Clock3, ListChecks } from "lucide-react";

import { Badge } from "@/src/components/shadcn/ui/badge";
import { ValerieCard } from "@/src/components/valerie-card";

import type { ReactNode } from "react";

export type ReferralWorkflowStatus =
  | "automated"
  | "verified"
  | "verify"
  | "needs_action"
  | "failed"
  | "waiting"
  | "not_run"
  | "not_needed";

/** USRC copy of the Confluent presentation, which is itself a copy of TPR's pending the shared refactor. */
export type ReferralWorkflowStep = {
  title: string;
  status: ReferralWorkflowStatus;
  summary: string;
  content?: ReactNode;
};

export function ReferralWorkflowLayout({ children }: { children: ReactNode }) {
  return <div className="flex w-full max-w-2xl flex-col gap-8 sm:min-w-[800px] mx-auto">{children}</div>;
}

const STATUS_PRESENTATION = {
  automated: {
    label: "Automated",
    className: "border-success-foreground/30 bg-success-foreground/10 text-success-foreground",
    icon: <CheckCircle2 />,
  },
  verified: {
    label: "Verified",
    className: "border-success-foreground/30 bg-success-foreground/10 text-success-foreground",
    icon: <CheckCircle2 />,
  },
  verify: {
    label: "Verify",
    className: "border-warn/30 bg-warn/10 text-warn-foreground",
    icon: <CircleAlert />,
  },
  needs_action: {
    label: "Needs action",
    className: "border-warn-foreground/30 bg-warn-foreground/10 text-warn-foreground",
    icon: <CircleAlert />,
  },
  failed: {
    label: "Failed",
    className: "border-destructive-foreground/30 bg-destructive-foreground/10 text-destructive-foreground",
    icon: <CircleAlert />,
  },
  waiting: {
    label: "Waiting",
    className: "border-border bg-muted text-muted-foreground",
    icon: <Clock3 />,
  },
  not_run: {
    label: "Not run",
    className: "border-border bg-muted text-muted-foreground",
    icon: <CircleDashed />,
  },
  not_needed: {
    label: "Not needed",
    className: "border-border bg-muted text-muted-foreground",
    icon: <CircleDashed />,
  },
} satisfies Record<ReferralWorkflowStatus, { label: string; className: string; icon: ReactNode }>;

function ReferralWorkflowStepCard({ step }: { step: ReferralWorkflowStep & { number: number } }) {
  const status = STATUS_PRESENTATION[step.status];
  const isSkipped = step.status === "not_needed";
  const cardVariant =
    step.status === "automated" || step.status === "verified"
      ? "success"
      : step.status === "failed"
        ? "destructive"
        : step.status === "needs_action" || step.status === "verify"
          ? "warn"
          : "outline";

  return (
    <ValerieCard
      title={`${step.number}. ${step.title}`}
      icon={step.status === "automated" ? undefined : status.icon}
      className={
        step.status === "not_run" || isSkipped ? "bg-muted/20 text-muted-foreground opacity-40 grayscale" : undefined
      }
      variant={cardVariant}
      actions={
        step.status === "automated" ? (
          <span className="text-success-foreground" aria-label="Automated">
            <CheckCircleIcon weight="fill" className="size-5" />
          </span>
        ) : (
          <Badge variant="outline" className={status.className}>
            {status.label}
          </Badge>
        )
      }
      contentClassName="space-y-3"
    >
      {isSkipped ? (
        <details>
          <summary className="cursor-pointer text-sm text-muted-foreground">{step.summary}</summary>
          <div className="mt-3 space-y-3">{step.content}</div>
        </details>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">{step.summary}</p>
          {step.content}
        </>
      )}
    </ValerieCard>
  );
}

export function ReferralWorkflowTimeline({
  steps,
  isProcessing = false,
  waitingMessage,
}: {
  steps: ReferralWorkflowStep[];
  isProcessing?: boolean;
  waitingMessage?: string;
}) {
  const numberedSteps = steps.map((step, index) => ({ ...step, number: index + 1 }));
  const actions = numberedSteps.filter((step) => ["needs_action", "failed", "verify"].includes(step.status));
  const hasFailure = steps.some((step) => step.status === "failed");
  const summaryVariant = hasFailure
    ? "destructive"
    : actions.length || waitingMessage
      ? "warn"
      : isProcessing
        ? "outline"
        : "success";

  return (
    <div className="space-y-8 pb-8">
      <ValerieCard
        title="Actions remaining"
        icon={<ListChecks />}
        variant={summaryVariant}
        contentClassName="space-y-4"
      >
        {waitingMessage ? (
          <p className="text-sm">{waitingMessage}</p>
        ) : actions.length ? (
          <ol className="space-y-2 text-sm">
            {actions.map((step) => (
              <li key={step.number}>
                <strong>
                  Step {step.number}: {step.title}
                </strong>
                <span className="block text-muted-foreground">{step.summary}</span>
              </li>
            ))}
          </ol>
        ) : (
          <p className="text-sm">
            {isProcessing
              ? "Automation is still running. This timeline will update as each workflow step completes."
              : "No manual actions remain."}
          </p>
        )}
      </ValerieCard>
      {numberedSteps.map((step) => (
        <ReferralWorkflowStepCard key={step.number} step={step} />
      ))}
    </div>
  );
}
